
import { inject, Injectable, DOCUMENT } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';

export interface SeoConfig {
  title: string;
  description: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  canonicalUrl?: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
  noIndex?: boolean;
}

// ============================================================
// Default SEO values (homepage)
// ============================================================
const DEFAULT_SEO: SeoConfig = {
  title: "Jadhu — India's Daily Digital Therapy Platform for Autism, ADHD & Neurodivergent Children",
  description:
    "Jadhu delivers 2 personalised sessions daily — real-world situations and skill-building games — for children and adults with autism, ADHD, ODD, and DCD. Continuous daily practice. 6 new situations every month. Free assessment. From ₹999/month.",
  keywords:
    'autism therapy app India, ADHD digital therapy India, neurodivergent daily practice, autism skills training mobile India, Jadhu, autism therapy Chennai, ADHD child programme Tamil Nadu, ODD therapy India, DCD support mobile',
  ogTitle: 'Jadhu — Real skills. Real situations. Every day.',
  ogDescription:
    "India's first anxiety-aware daily digital therapy platform. 14 real-world situations in Month 1. 360 sessions in 6 months. Continuous daily practice for as long as your child grows.",
  // ogImage: `${environment.websiteUrl}/images/og-banner.jpg?v=4`,
  ogUrl: `${environment.websiteUrl}/?v=4`,
  canonicalUrl: `${environment.websiteUrl}/`,
};

// ============================================================
// Predefined schemas
// ============================================================
export const JADHU_SCHEMAS = {

  // ============================================================
  // Organization Schema
  // ============================================================
  organization: {
    '@context': 'https://schema.org',
    '@type': 'Organization',

    '@id': `${environment.websiteUrl}/#organization`,

    name: 'Jadhu',

    url: `${environment.websiteUrl}`,

    logo: `${environment.websiteUrl}/assets/logo/jadhu-logo.svg`,

    description:
      "India's daily digital therapy platform for autism, ADHD, ODD and neurodivergent children.",

    sameAs: [
      `${environment.websiteUrl}/instagram`,
      `${environment.websiteUrl}/linkedin`
    ],

    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'support@jadhuhealth.com',
    },

    areaServed: {
      '@type': 'Country',
      name: 'India',
    },

    inLanguage: ['en', 'ta', 'hi'],
  },

  // ============================================================
  // Website Schema
  // ============================================================
  website: {
    '@context': 'https://schema.org',

    '@type': 'WebSite',

    '@id': `${environment.websiteUrl}/#website`,

    name: 'Jadhu',

    url: `${environment.websiteUrl}`,

    publisher: {
      '@id': `${environment.websiteUrl}/#organization`,
    },

    inLanguage: 'en',
  },

  // ============================================================
  // Medical Organization Schema
  // ============================================================

  medicalOrganization: {
    '@context': 'https://schema.org',

    '@type': 'MedicalOrganization',

    '@id':
      `${environment.websiteUrl}/#medicalorganization`,

    name: 'Jadhu',

    url: `${environment.websiteUrl}/`,

    description:
      'Clinically designed digital therapy platform for neurodivergent learners.',

    medicalSpecialty: [
      'http://schema.org/Pediatric',
      'http://schema.org/Psychiatric'
    ],

    areaServed: {
      '@type': 'Country',
      name: 'India'
    }
  },

  // ============================================================
  // Software Application Schema
  // ============================================================
  softwareApplication: {
    '@context': 'https://schema.org',

    '@type': 'SoftwareApplication',

    '@id': `${environment.websiteUrl}/#softwareapplication`,

    name: 'Jadhu',

    applicationCategory: 'HealthApplication',

    operatingSystem: 'Android, iOS',

    description:
      "India's daily digital therapy platform for neurodivergent children and adults with Autism, ADHD, ODD, and DCD.",

    offers: {
      '@type': 'Offer',
      price: '999',
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock',
    },

    areaServed: {
      '@type': 'Country',
      name: 'India',
    },

    inLanguage: ['en', 'ta', 'hi'],
  },

  // ============================================================
  // Service Schema
  // ============================================================
  service: (
    name: string,
    description: string
  ) => ({
    '@context': 'https://schema.org',

    '@type': 'Service',

    name,

    description,

    provider: {
      '@id': `${environment.websiteUrl}/#organization`,
    },

    areaServed: {
      '@type': 'Country',
      name: 'India',
    },
  }),

  // ============================================================
  // FAQ Schema
  // ============================================================
  faqPage: (faqs: { question: string; answer: string }[]) => ({
    '@context': 'https://schema.org',

    '@type': 'FAQPage',

    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',

      name: faq.question,

      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  }),

  // ============================================================
  // Breadcrumb Schema
  // ============================================================
  breadcrumb: (
    items: { name: string; url: string }[]
  ) => ({
    '@context': 'https://schema.org',

    '@type': 'BreadcrumbList',

    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',

      position: index + 1,

      name: item.name,

      item: item.url,
    })),
  }),
};

// ============================================================
// SEO Service
// ============================================================
@Injectable({ providedIn: 'root' })
export class SeoService {
  private title = inject(Title);
  private meta = inject(Meta);
  private doc = inject(DOCUMENT);

  /**
   * Set all SEO tags for a page.
   * Call this in ngOnInit of each page component.
   */
  setPage(config: Partial<SeoConfig>): void {
    const merged: SeoConfig = { ...DEFAULT_SEO, ...config };

    this.setTitle(merged.title);
    this.setMeta(merged);
    this.setCanonical(merged.canonicalUrl ?? merged.ogUrl ?? '');

    if (merged.schema) {
      this.setSchema(merged.schema);
    }
  }

  // --- Title ---
  private setTitle(title: string): void {
    this.title.setTitle(title);
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ name: 'twitter:title', content: title });
  }

  // --- Meta tags ---
  private setMeta(config: SeoConfig): void {
    // Standard
    this.meta.updateTag({ name: 'description', content: config.description });

    if (config.keywords) {
      this.meta.updateTag({ name: 'keywords', content: config.keywords });
    }

    if (config.noIndex) {
      this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' });
    } else {
      this.meta.updateTag({ name: 'robots', content: 'index, follow, max-image-preview:large' });
    }

    // Open Graph
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'Jadhu' });
    this.meta.updateTag({ property: 'og:locale', content: 'en_IN' });
    this.meta.updateTag({ property: 'og:description', content: config.ogDescription ?? config.description });
    this.meta.updateTag({ property: 'og:url', content: config.ogUrl ?? '' });

    if (config.ogImage) {
      this.meta.updateTag({ property: 'og:image', content: config.ogImage });
      this.meta.updateTag({ property: 'og:image:width', content: '1200' });
      this.meta.updateTag({ property: 'og:image:height', content: '630' });
      this.meta.updateTag({ property: 'og:image:alt', content: config.ogTitle ?? config.title });
    }

    // Twitter Card
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:description', content: config.ogDescription ?? config.description });

    if (config.ogImage) {
      this.meta.updateTag({ name: 'twitter:image', content: config.ogImage });
    }
  }

  // --- Canonical URL ---
  private setCanonical(url: string): void {
    if (!url || typeof this.doc === 'undefined') return;

    let link: HTMLLinkElement | null = this.doc.querySelector("link[rel='canonical']");

    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }

    link.setAttribute('href', url);
  }

  // --- JSON-LD Structured Data ---
  setSchema(schema: Record<string, unknown> | Record<string, unknown>[]): void {
    if (typeof this.doc === 'undefined') return;

    // Remove existing schema scripts to avoid duplicates
    const existing = this.doc.querySelectorAll('script[type="application/ld+json"][data-jadhu]');
    existing.forEach((el) => el.remove());

    const schemas = Array.isArray(schema) ? schema : [schema];

    schemas.forEach((s) => {
      const script = this.doc.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-jadhu', 'true');
      script.textContent = JSON.stringify(s);
      this.doc.head.appendChild(script);
    });
  }

  // --- Hreflang (multilingual: EN / Tamil / Hindi) ---
  setHreflang(path: string): void {
    if (typeof this.doc === 'undefined') return;

    const langs = [
      { lang: 'en', href: `${environment.websiteUrl}${path}` },
      { lang: 'ta', href: `${environment.websiteUrl}/ta${path}` },
      { lang: 'hi', href: `${environment.websiteUrl}/hi${path}` },
      { lang: 'x-default', href: `${environment.websiteUrl}${path}` },
    ];

    // Remove old hreflang links
    const existing = this.doc.querySelectorAll("link[rel='alternate'][hreflang]");
    existing.forEach((el) => el.remove());

    langs.forEach(({ lang, href }) => {
      const link = this.doc.createElement('link');
      link.rel = 'alternate';
      link.setAttribute('hreflang', lang);
      link.setAttribute('href', href);
      this.doc.head.appendChild(link);
    });
  }
}
